/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import clsx from 'clsx';
import { ChevronLeft, Star, Ticket } from 'lucide-react';
import { useMemo } from 'react';

/**
 * Internal dependencies
 */
import { MediaGallery } from '@Components/MediaGallery';
import { formatCurrency, formatZonedDateTime } from '@Utils/utils';
import GoogleMapContainer from '@Components/GoogleMapContainer';
import type { EventPreviewProps } from './EventPreview';

interface EventsSitePreviewProps extends EventPreviewProps {
  /** When true, renders the full 2-column desktop layout. Defaults to false (single-column for phone preview). */
  fullPage?: boolean;
}

const getPriceRange = (ticketTypes?: { price: number }[]): string | null => {
  if (!ticketTypes || ticketTypes.length === 0) {
    return null;
  }
  const prices = ticketTypes.map((type) => Number(type.price));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  return minPrice === maxPrice
    ? formatCurrency(minPrice)
    : `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
};

export default function EventsSitePreview({
  event,
  venue,
  fullPage = false,
}: EventsSitePreviewProps) {
  const priceRange = useMemo(
    () => getPriceRange(event.ticketTypes),
    [event.ticketTypes],
  );

  return (
    <div className="bg-zinc-50 pb-16">
      {/* Preview Banner */}
      {fullPage && (
        <div className="bg-amber-100 px-4 py-2 text-center text-sm font-medium text-amber-800">
          This is a preview — what you see here is how the event will appear on
          the events site.
        </div>
      )}
      {/* Header */}
      <nav className="w-full bg-black px-4 py-3">
        <div className="container mx-auto flex items-center justify-between px-4">
          <div className="flex items-center">
            <img
              src="/by-landmark-white.webp"
              alt="By Landmark"
              className="h-16 w-auto"
            />
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 pt-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => undefined}
          >
            <ChevronLeft className="h-4 w-4" />
            Back to events
          </Button>
        </div>

        <div
          className={clsx(
            'grid grid-cols-1 gap-8',
            fullPage && 'lg:grid-cols-3',
          )}
        >
          {/* Main Content */}
          <div className={clsx(fullPage && 'lg:col-span-2')}>
            {/* Event Featured Image */}
            {event.featuredImage && (
              <div className="mb-6 overflow-hidden rounded-lg">
                <CloudinaryImage
                  publicId={event.featuredImage.cloudinaryId}
                  width={1200}
                  aspectRatio="16/9"
                  mode="scale"
                  className={clsx('w-full object-cover')}
                />
              </div>
            )}

            {/* Title and Date */}
            <div className="mb-6">
              <Heading level="h2" className="mb-2">
                {event.name}
              </Heading>
              {event.visibility === 'MEMBERS_ONLY' && (
                <Badge className="mb-3 bg-[#CAAB6A]">
                  <Star className="mr-1 inline h-3 w-3" /> Members Only
                </Badge>
              )}
              {event.startDateTime && event.endDateTime && (
                <p className="text-lg text-zinc-600">
                  {formatZonedDateTime(event.startDateTime, 'MMMM d')} @{' '}
                  {formatZonedDateTime(event.startDateTime, 'h:mm a')} -{' '}
                  {formatZonedDateTime(event.endDateTime, 'h:mm a')}
                </p>
              )}
            </div>

            {/* Tickets Section */}
            <div className="mb-8">
              {event.ticketTypes && event.ticketTypes.length > 0 ? (
                <>
                  <Heading level="h2" styleAs="h3" className="mb-4">
                    Tickets
                  </Heading>
                  <div className="space-y-4">
                    {event.ticketTypes.map((ticketType) => (
                      <div key={ticketType.id} className="relative">
                        <div className="flex overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100 shadow-sm">
                          <div className="relative flex w-8 items-center justify-center border-r-2 border-dashed border-zinc-200 bg-zinc-100" />
                          <div className="flex flex-1 flex-col justify-between gap-2 p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div className="shrink grow">
                                <div className="mb-1 text-lg font-semibold">
                                  {ticketType.name}
                                </div>
                                {ticketType.description && (
                                  <div className="text-sm text-zinc-500">
                                    {ticketType.description}
                                  </div>
                                )}
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <Ticket className="h-5 w-5 -rotate-45 text-[#caab6a]" />
                                <div className="text-lg font-semibold">
                                  {formatCurrency(ticketType.price)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="absolute -top-0.5 left-7 z-10 h-1.5 w-2 rounded-b-full bg-zinc-50" />
                        <div className="absolute -bottom-0.5 left-7 z-10 h-1.5 w-2 rounded-t-full bg-zinc-50" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-8">
                    <Button
                      size="lg"
                      className="group w-full bg-[#caab6a] hover:bg-[#b8995a] sm:w-auto"
                      onClick={() => undefined}
                    >
                      <Ticket className="h-5 w-5 text-white transition-transform duration-300 group-hover:scale-110" />
                      <span>Get Tickets</span>
                    </Button>
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-[#caab6a]/30 bg-linear-to-br from-[#caab6a]/5 to-[#caab6a]/10 p-6 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#caab6a]/15">
                    <Ticket className="h-6 w-6 text-[#caab6a]" />
                  </div>
                  <Heading level="h3" styleAs="h4" className="mb-1">
                    Free Event
                  </Heading>
                  <p className="text-sm text-zinc-500">
                    No tickets required — just show up and enjoy!
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="mb-8">
              <div
                className="prose max-w-none text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            </div>

            {/* Media Gallery */}
            {event.images && event.images.length > 0 && (
              <div className="mb-8">
                <Card>
                  <CardHeader>
                    <Heading level="h3" styleAs="h4">
                      Media Gallery
                    </Heading>
                  </CardHeader>
                  <CardContent>
                    <MediaGallery
                      images={event.images}
                      columns={{ base: 3, sm: 4, md: 5 }}
                      gapClassName="gap-3"
                      thumbAspect="square"
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Sidebar cards inline (only in phone preview) */}
            {!fullPage && (
              <>
                <Card className="mb-6">
                  <CardHeader>
                    <Heading level="h4" styleAs="h5">
                      Details
                    </Heading>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {event.startDateTime && (
                      <div>
                        <dt className="text-sm font-semibold text-zinc-700">
                          Date:
                        </dt>
                        <dd className="text-sm text-zinc-600">
                          {formatZonedDateTime(event.startDateTime, 'MMMM d')}
                        </dd>
                      </div>
                    )}
                    {event.startDateTime && event.endDateTime && (
                      <div>
                        <dt className="text-sm font-semibold text-zinc-700">
                          Time:
                        </dt>
                        <dd className="text-sm text-zinc-600">
                          {formatZonedDateTime(event.startDateTime, 'h:mm a')} -{' '}
                          {formatZonedDateTime(event.endDateTime, 'h:mm a')}
                        </dd>
                      </div>
                    )}
                    {event.ticketTypes && event.ticketTypes.length > 0 && (
                      <div>
                        <dt className="text-sm font-semibold text-zinc-700">
                          Cost:
                        </dt>
                        <dd className="text-sm text-zinc-600">
                          {priceRange || 'Price not available'}
                        </dd>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {venue && (
                  <Card>
                    <CardHeader>
                      <Heading level="h4" styleAs="h5">
                        Venue
                      </Heading>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <dd className="text-sm font-semibold text-zinc-900">
                          {venue.name}
                        </dd>
                      </div>
                      {venue.toastRestaurant && (
                        <div>
                          <dd className="text-sm text-zinc-600">
                            Location details available on the events site
                          </dd>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Sidebar (only in full-page mode) */}
          {fullPage && (
            <div className="lg:col-span-1">
              <div className="sticky top-12">
                <Card className="mb-6">
                  <CardHeader>
                    <Heading level="h4" styleAs="h5">
                      Details
                    </Heading>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {event.startDateTime && (
                      <div>
                        <dt className="text-sm font-semibold text-zinc-700">
                          Date:
                        </dt>
                        <dd className="text-sm text-zinc-600">
                          {formatZonedDateTime(event.startDateTime, 'MMMM d')}
                        </dd>
                      </div>
                    )}
                    {event.startDateTime && event.endDateTime && (
                      <div>
                        <dt className="text-sm font-semibold text-zinc-700">
                          Time:
                        </dt>
                        <dd className="text-sm text-zinc-600">
                          {formatZonedDateTime(event.startDateTime, 'h:mm a')} -{' '}
                          {formatZonedDateTime(event.endDateTime, 'h:mm a')}
                        </dd>
                      </div>
                    )}
                    {event.ticketTypes && event.ticketTypes.length > 0 && (
                      <div>
                        <dt className="text-sm font-semibold text-zinc-700">
                          Cost:
                        </dt>
                        <dd className="text-sm text-zinc-600">
                          {priceRange || 'Price not available'}
                        </dd>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {venue && (
                  <>
                    <Card>
                      <CardHeader>
                        <Heading level="h4" styleAs="h5">
                          Venue
                        </Heading>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <dd className="text-sm font-semibold text-zinc-900">
                            {venue.name}
                          </dd>
                        </div>
                        {venue.toastRestaurant && (
                          <>
                            <div>
                              <dd className="text-sm text-zinc-600">
                                {venue.toastRestaurant.location?.address1}{' '}
                                {venue.toastRestaurant.location?.address2 && (
                                  <>{venue.toastRestaurant.location.address2}</>
                                )}
                              </dd>
                              <dd className="text-sm text-zinc-600">
                                {venue.toastRestaurant.location?.city},{' '}
                                {venue.toastRestaurant.location?.stateCode}{' '}
                                {venue.toastRestaurant.location?.zipCode}
                              </dd>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {/* Map */}
                    {venue.toastRestaurant &&
                      venue.toastRestaurant.location?.latitude &&
                      venue.toastRestaurant.location?.longitude && (
                        <div className="mt-6">
                          <div className="overflow-hidden rounded-md">
                            <GoogleMapContainer
                              lat={venue.toastRestaurant.location.latitude}
                              lng={venue.toastRestaurant.location.longitude}
                              zoom={15}
                            />
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cancellation Policy */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <Heading level="h3" styleAs="h4">
                Cancellation Policy
              </Heading>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="mt-1 text-sm text-zinc-600">
                    Cancellations up to 6 days prior to the event will receive a
                    full refund. Refunds are typically reflected in your account
                    in 5-7 business days.
                  </p>
                </div>
                <div>
                  <p className="mt-1 text-sm text-zinc-600">
                    Cancellations requested 0-5 days prior to the event will
                    receive a gift card as credit. Gift cards can be used at any
                    By Landmark restaurant location. Physical gift cards are
                    mailed USPS. Please allow 14 business days to be delivered.
                  </p>
                </div>
                <div>
                  <p className="mt-1 text-sm text-zinc-600">
                    Any questions, please email{' '}
                    <span className="underline">play@bylandmark.com</span>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
